/**
 * ReferenceSelect Component
 *
 * Lightweight, performance-optimized searchable select for entity reference
 * selection within ArTable cells. Stripped-down version of EntityRecordUI
 * with external-only save mode and minimal state.
 *
 * Key differences from EntityRecordUI:
 * - External onChange only (no internal Firestore saves)
 * - Cache/search orchestration via `useEntityRecordRegistry` (Algolia + cache)
 * - No dirty tracking or PendingSaves integration
 * - Returns ArEntityReference directly for cell storage
 *
 * While searching, a `CREATE_RECORD_OPTION_VALUE` sentinel option lets the user
 * create a record named with the typed query and select it inline, reusing the
 * normal `emitSelection` pick path.
 *
 * Invariant: `toggleMode` is the only mode switch this component owns. Three
 * controls call it (SYD-2376): the reference popup's "Manual entry" footer, the
 * manual-entry sidebar's "Switch to Reference", and the record sidebar's
 * "Switch to Manual Entry". The row context menu is a second, value-level path: it
 * holds no ReferenceSelect instance and writes the cell through
 * `pasteDownColumn`, which this component then reads back as a change of
 * `value`.
 *
 * Invariant: manual mode is one branch with no menu — a text input and a pen
 * that opens the manual-entry sidebar on a single click. Being a pen is also
 * what tells a typed-in value apart from a picked record. The sidebar opens
 * whether or not the entity carries a layout, because it holds the only
 * "Switch to Reference" a manual cell can reach.
 *
 * Invariant: inside an ArTable no control in this component is tab-reachable —
 * `useKeyboardNavigation` spends Tab moving between cells — so every affordance
 * needs a `field.*` binding of its own. `field.openDetail` opens this field's
 * detail in both modes — the record sidebar in reference mode, the manual-entry
 * sidebar in manual mode. ArrowDown (`field.openPopup`) stays the
 * reference-mode popup's alone: a run of manual cells needs it for moving down
 * the column.
 */

import {
  FC,
  useState,
  useContext,
  useMemo,
  useCallback,
  memo,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Button, Input, Select, Space, Typography, theme } from 'antd';
import { proxy, useSnapshot } from 'valtio';
import { SidebarControlContext } from '@/providers/SidebarControlContext';
import { UserContext } from '@/providers/UserContext';
import { MessageContext } from '@/providers/MessageContext';
import { entityRecordActions } from '@/store';
import { useKeybindings, ariaKeyshortcutsFor } from '@/keyboard';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';
import { isEntityReference, isManualEntryValue } from '#types/System.Artifacts.types';
import type { ArCell as ArCellType, ArCellValue, ArEntityReference, ArManualEntryValue } from '#types/System.Artifacts.types';
import type { EntityRecord, ReferencedDocument } from '#types/System.types';
import type { ReferenceCandidate } from '#types';
import Icon from '@/components/atoms/Icon';
import { CREATE_RECORD_OPTION_VALUE, CreateRecordOptionLabel } from '@/components/createRecordOption';
import { useGet } from '@/use/data/useGet';
import { useEntityRecordRegistry } from '@/store/entityRecordsRegistry';
import { openCompositeFieldSidebar } from '@/components/CompositeField/compositeFieldSidebarState';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { ArTableUIStateContext } from '../../context';
import type { SelectionState } from '../../state/createArTableState';

const MAX_DROPDOWN_OPTIONS = 100;

const { Text } = Typography;

/**
 * Fallback selection proxy for ReferenceSelect instances rendered outside an
 * ArTable (agent review, RelationshipTree), where ArTableUIStateContext is null.
 * Keeps the multi-row-selection check reactive and hook-safe in both cases.
 */
const EMPTY_SELECTION: SelectionState = proxy({
  selectedRows: {},
  selectedRowIds: [],
  selectedRowIndexById: {},
  selectedCount: 0,
  lastClickedRowId: null,
  scope: 'visible',
});

/** A dropdown option carrying an optional Typesafe match probability for the "Top matches" tier. */
interface ReferenceSelectOption {
  value: string;
  label: string;
  probability?: number;
}

interface ReferenceSelectOptionGroup {
  label: string;
  options: ReferenceSelectOption[];
}

function isBlank(s: string | undefined | null): boolean {
  return s == null || s.trim() === '';
}

interface ReferenceSelectProps {
  /** DOM id for keyboard navigation focus targeting */
  id?: string;
  /**
   * Entity ID to fetch records from.
   *
   * **Performance contract:** This prop must be stable for the lifetime of the component instance.
   * If it can change, the caller must provide a `key` derived from `entityId/entityGroupId` to force remount.
   */
  entityId: string;
  /**
   * Optional entity group ID for filtering.
   *
   * **Performance contract:** Must be stable for the lifetime of the component instance.
   * If it can change, the caller must provide a `key` derived from `entityId/entityGroupId` to force remount.
   */
  entityGroupId?: string;
  /** Current cell value - accepts wide ArCellValue, extracts docId defensively */
  value: ArCellValue;
  /**
   * Typesafe-judged record candidates for the extracted value, shown as a
   * "Top matches" group above the live search results (query-filtered, deduped
   * against live hits). The match probability renders only in the option body,
   * never in the persisted label.
   */
  candidates?: ReferenceCandidate[];
  /** When true, disables manual entry mode */
  disableManualEntry?: boolean;
  /** Callback when selection changes - emits ArCellValue (and optionally full referenced document for element mapping) */
  onChange: (
    value: ArCellValue,
    referencedDocument?: ReferencedDocument | null
  ) => void;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** CSS style override */
  style?: React.CSSProperties;
  /** Native hover line on the cell wrapper, used by Form Derive projected cells. */
  title?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * Materializes the cell on the live Valtio proxy and returns it, so the manual
   * entry sidebar subscribes to the live cell value. Called only from the open
   * handler — the owner may not write the proxy during render.
   */
  resolveCellValueProxy?: () => ArCellType;
}

type ReferenceSelectMode = 'reference' | 'manual';

/**
 * ReferenceSelect - Optimized entity reference selector for table cells.
 *
 * Uses `useEntityRecordRegistry` for cache + search orchestration.
 * Returns ArCellValue on selection for direct cell storage.
 */
const ReferenceSelect: FC<ReferenceSelectProps> = ({
  id,
  entityId,
  entityGroupId,
  value,
  candidates,
  disableManualEntry = false,
  onChange,
  disabled = false,
  style,
  title,
  onFocus,
  onBlur,
  resolveCellValueProxy,
}) => {
  const { isPdfExportMode } = usePdfExportContext();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const lastRequestedRecordIdRef = useRef<string | null>(null);
  const [modeOverride, setModeOverride] = useState<ReferenceSelectMode | null>(null);
  const { get } = useGet();
  const { user } = useContext(UserContext);
  const { handleFirestoreWriteError } = useContext(MessageContext);
  const sidebarControl = useContext(SidebarControlContext);
  const arTableUIState = useContext(ArTableUIStateContext);
  const selectionSnap = useSnapshot(arTableUIState?.selection ?? EMPTY_SELECTION);
  const isMultiRowSelection = selectionSnap.selectedCount > 1;
  const { token } = theme.useToken();

  const {
    searchQuery,
    setSearchQuery,
    entityRecords,
    loading,
    error,
    getRecord,
  } = useEntityRecordRegistry({
    entityId,
    entityGroupId,
  });

  const { entities } = useContext(EntitiesContext);
  const entityLayout = entities[entityId]?.defaultUILayout?.structure;

  const manualInputValue = isManualEntryValue(value) ? value.value ?? '' : '';

  const handleManualEntryChange = useCallback((value: ArCellValue) => {
    onChange(value);
  }, [onChange]);

  const baseMode: ReferenceSelectMode = isManualEntryValue(value) ? 'manual' : 'reference';

  // Track the baseMode at the time modeOverride was set.
  // When value type changes externally, modeOverrideBaseMode won't match baseMode,
  // so we ignore the stale modeOverride and use baseMode instead.
  const [modeOverrideBaseMode, setModeOverrideBaseMode] = useState<ReferenceSelectMode | null>(null);

  const mode = useMemo(() => {
    if (disableManualEntry) return 'reference';
    // If modeOverride exists and was set when baseMode was the same, use it
    if (modeOverride !== null && modeOverrideBaseMode === baseMode) {
      return modeOverride;
    }
    // Otherwise use baseMode (either no override, or override is stale)
    return baseMode;
  }, [disableManualEntry, modeOverride, modeOverrideBaseMode, baseMode]);

  const selectedDocId = useMemo((): string | undefined => {
    if (value == null) return undefined;
    if (isEntityReference(value)) return value.docId;
    if (typeof value === 'string') return value;
    return undefined;
  }, [value]);

  const isValueMalformed = useMemo(() => {
    if (mode !== 'reference') return false;
    if (value == null) return false;
    if (selectedDocId) return false;
    if (typeof value === 'string') return false;
    if (isManualEntryValue(value)) return false;
    return true;
  }, [mode, selectedDocId, value]);

  const liveOptions = useMemo<ReferenceSelectOption[]>(() => {
    return entityRecords
      .filter(rec => rec.docId)
      .slice(0, MAX_DROPDOWN_OPTIONS)
      .map(rec => ({
        value: rec.docId!,
        label: rec.name || 'Unnamed Record',
      }));
  }, [entityRecords]);

  const topMatches = useMemo<ReferenceSelectOption[]>(() => {
    if (!candidates?.length) return [];
    const live = new Set(liveOptions.map((o) => o.value));
    const query = searchQuery.trim().toLowerCase();
    return candidates
      .filter((c) => !live.has(c.docId))
      .filter((c) => (query ? c.name.toLowerCase().includes(query) : true))
      .map((c) => ({ value: c.docId, label: c.name, probability: c.probability }));
  }, [candidates, liveOptions, searchQuery]);

  const options = useMemo<(ReferenceSelectOption | ReferenceSelectOptionGroup)[]>(() => {
    const query = searchQuery.trim();
    const createOption: ReferenceSelectOption | null = isBlank(query)
      ? null
      : { value: CREATE_RECORD_OPTION_VALUE, label: `Create New Record "${query}"` };
    const base: (ReferenceSelectOption | ReferenceSelectOptionGroup)[] =
      topMatches.length === 0
        ? liveOptions
        : [
          { label: 'Top matches', options: topMatches },
          { label: 'Search results', options: liveOptions },
        ];
    return createOption ? [...base, createOption] : base;
  }, [topMatches, liveOptions, searchQuery]);

  const optionByValue = useMemo<Map<string, ReferenceSelectOption>>(
    () => new Map([...topMatches, ...liveOptions].map((o) => [o.value, o])),
    [topMatches, liveOptions],
  );

  const emitSelection = useCallback(
    async ({
      targetEntityId,
      targetRecordId,
      targetGroupId,
      suggestedName,
    }: {
      targetEntityId: string;
      targetRecordId: string | undefined;
      targetGroupId: string | undefined;
      suggestedName: string | undefined;
    }) => {
      if (!targetRecordId) {
        lastRequestedRecordIdRef.current = null;
        onChange(null);
        return;
      }

      const selectedRecord = entityRecords.find(
        (rec) => rec.docId === targetRecordId
      );

      lastRequestedRecordIdRef.current = targetRecordId;

      const record = await get<EntityRecord>(`entities/${targetEntityId}/records`, targetRecordId);

      if (lastRequestedRecordIdRef.current !== targetRecordId) {
        return;
      }

      const candidateName = [suggestedName, selectedRecord?.name, record?.name].find(
        (n) => !isBlank(n)
      );
      if (!candidateName) {
        console.warn(
          `[ReferenceSelect] No name available for ${targetEntityId}/${targetRecordId}; omitting cacheLabel`
        );
      }

      const reference: ArEntityReference = {
        referenceType: 'entity',
        docId: targetRecordId,
        ...(candidateName && { cacheLabel: candidateName }),
        ...(targetGroupId && { entityGroupId: targetGroupId }),
        ...(record?.fields && { fields: record.fields }),
        ...(record?.entityReferences && { entityReferences: record.entityReferences }),
      };

      onChange(reference, record ?? null);
    },
    [entityRecords, get, onChange]
  );

  const handleChange = useCallback(
    async (newRecordId: string | undefined) => {
      if (newRecordId === CREATE_RECORD_OPTION_VALUE) {
        const name = searchQuery.trim();
        if (!name) return;
        try {
          const createdRecordId = await entityRecordActions.createEntityRecord(
            entityId,
            user?.uid,
            entityGroupId,
            name
          );
          setIsSelectOpen(false);
          setSearchQuery('');
          await emitSelection({
            targetEntityId: entityId,
            targetRecordId: createdRecordId,
            targetGroupId: entityGroupId,
            suggestedName: name,
          });
        } catch (error) {
          handleFirestoreWriteError(error, '[ReferenceSelect] createEntityRecord');
        }
        return;
      }

      const suggestedName = newRecordId
        ? optionByValue.get(newRecordId)?.label
        : undefined;

      await emitSelection({
        targetEntityId: entityId,
        targetRecordId: newRecordId,
        targetGroupId: entityGroupId,
        suggestedName,
      });
    },
    [emitSelection, entityId, entityGroupId, handleFirestoreWriteError, optionByValue, searchQuery, setSearchQuery, user]
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsSelectOpen(open);
    if (open) {
      setSearchQuery('');
    }
  }, [setSearchQuery]);

  const displayName = useMemo((): string => {
    if (!selectedDocId) return '';
    if (isEntityReference(value) && !isBlank(value.cacheLabel)) return value.cacheLabel!;
    const record =
      entityRecords.find((r) => r.docId === selectedDocId) ??
      getRecord(entityId, selectedDocId, entityGroupId);
    if (!record) return '';
    if (isBlank(record.name)) {
      console.warn(
        `[ReferenceSelect] Record ${entityId}/${selectedDocId} has no name; using fallback`
      );
      return `Unnamed ${selectedDocId}`;
    }
    return record.name!;
  }, [selectedDocId, value, entityId, entityGroupId, getRecord, entityRecords]);

  const toggleMode = useCallback(() => {
    if (disabled || disableManualEntry) return;
    setIsSelectOpen(false);
    setSearchQuery('');
    const next = mode === 'reference' ? 'manual' : 'reference';

    // Record the baseMode at the time of override so we can detect stale overrides
    setModeOverrideBaseMode(baseMode);

    if (next === 'manual') {
      setModeOverride('manual');
      return;
    }

    onChange(null);
    setModeOverride('reference');
    setIsSelectOpen(true);
  }, [baseMode, disabled, disableManualEntry, mode, onChange, setSearchQuery]);

  /**
   * Opens the manual-entry sidebar, with or without a layout to render: an
   * entity that carries no `defaultUILayout` still needs the sidebar's
   * "Switch to Reference", which is the only way out of manual mode.
   */
  const handleOpenEntitySidebar = useCallback(() => {
    if (disabled) return;
    openCompositeFieldSidebar({
      entityId,
      entityStructure: entityLayout,
      cellValueProxy: resolveCellValueProxy?.(),
      onChange: handleManualEntryChange,
      fieldId: entityId,
      title: entities[entityId]?.description?.shortLabel,
      onSwitchToReference: disableManualEntry ? undefined : toggleMode,
    });
  }, [disabled, entityLayout, resolveCellValueProxy, entityId, handleManualEntryChange, entities, disableManualEntry, toggleMode]);

  /**
   * Handles manual input changes.
   * Emits ArManualEntryValue for non-empty strings, preserving structured
   * fields/entityReferences on an existing manual object. Clearing the text
   * keeps the object when structure exists, otherwise emits null.
   */
  const handleManualChange = useCallback(
    (nextValue: string) => {
      const current = isManualEntryValue(value) ? value : undefined;
      const structure = current
        ? {
          ...(current.fields && { fields: current.fields }),
          ...(current.entityReferences && { entityReferences: current.entityReferences }),
        }
        : {};
      const hasStructure = Object.keys(structure).length > 0;
      if (nextValue) {
        onChange({ valueType: 'manual', ...structure, value: nextValue } satisfies ArManualEntryValue);
        return;
      }
      onChange(hasStructure ? ({ valueType: 'manual', ...structure } satisfies ArManualEntryValue) : null);
    },
    [onChange, value]
  );

  const handleOpenRecord = useCallback(() => {
    if (disabled || !entityId) return;

    setIsSelectOpen(false);

    if (!sidebarControl) {
      console.warn('[ReferenceSelect] SidebarControl not available');
      return;
    }

    sidebarControl.openEntitySidebar(
      entityId,
      selectedDocId ?? undefined,
      entityGroupId,
      displayName || undefined,
      async (callbackEntityId, callbackRecordId, callbackGroupId, callbackRecordName) => {
        const entityMatches = callbackEntityId === entityId;
        const groupMatches = callbackGroupId === entityGroupId;

        if (!entityMatches || !groupMatches) {
          console.error('[ReferenceSelect] Sidebar entity/group changed, not updating original component');
          return;
        }

        await emitSelection({
          targetEntityId: callbackEntityId,
          targetRecordId: callbackRecordId,
          targetGroupId: callbackGroupId,
          suggestedName: callbackRecordName,
        });
      },
      undefined,
      disableManualEntry ? undefined : toggleMode
    );
  }, [
    disabled,
    entityId,
    sidebarControl,
    selectedDocId,
    entityGroupId,
    displayName,
    emitSelection,
    disableManualEntry,
    toggleMode,
  ]);

  const { onKeyDown: referenceKeyDown } = useKeybindings({
    'field.openDetail': handleOpenRecord,
    ...(isSelectOpen ? {} : { 'field.openPopup': () => setIsSelectOpen(true) }),
  });

  /**
   * Manual mode's keyboard route to the sidebar, and the only one there is:
   * ArTable owns Tab through `useKeyboardNavigation`, which commits the cell and
   * moves to the next column, so the pen is never tab-reachable.
   * `field.openDetail` carries the same meaning it has in reference mode — open
   * this field's detail — and leaves ArrowDown to column navigation, which a run
   * of manual cells needs far more than it needs a second way in (SYD-2376).
   */
  const { onKeyDown: manualKeyDown } = useKeybindings({
    'field.openDetail': handleOpenEntitySidebar,
  });

  if (isPdfExportMode) {
    const pdfStyle: React.CSSProperties = {
      ...style,
      padding: '1px 2px',
      color: '#000',
      fontSize: 'inherit',
      lineHeight: '1.4',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
    };

    if (mode === 'reference') {
      return <div style={pdfStyle}>{displayName || '—'}</div>;
    }

    return <div style={{ ...pdfStyle, fontStyle: 'italic' }}>{manualInputValue || '—'}</div>;
  }

  /** Names the cell's entity (and group) so every control's accessible name stays unique down a rendered table's columns. */
  const entityScope = `${entityId}${entityGroupId ? ` ${entityGroupId}` : ''}`;

  return (
    mode === 'reference' ? (
      <Space.Compact title={title} style={{ width: '100%', ...style }}>
        <Select
          id={id}
          aria-label={`Select ${entityScope} record`}
          aria-keyshortcuts={ariaKeyshortcutsFor('field.openDetail')}
          onKeyDown={referenceKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          open={isSelectOpen}
          onOpenChange={handleOpenChange}
          style={{ flex: 1, minWidth: 0 }}
          styles={{
            popup: {
              root: {
                width: 400,
              },
            },
          }}
          variant="borderless"
          showSearch={{
            filterOption: false,
            searchValue: searchQuery,
            onSearch: setSearchQuery,
          }}
          allowClear
          placeholder="Search Records"
          onChange={handleChange}
          value={selectedDocId}
          disabled={disabled}
          virtual={true}
          listHeight={300}
          notFoundContent={
            isValueMalformed
              ? 'Invalid reference'
              : loading
                ? 'Loading...'
                : error
                  ? `Error: ${error instanceof Error ? error.message : String(error)}`
                  : !entityId
                    ? 'No entity selected'
                    : 'No results found'
          }
          popupRender={(menu) => (
            <div>
              {menu}
              <div style={{ display: 'flex', flexDirection: 'row', gap: token.margin }}>
                {!disableManualEntry ? (
                  <Button
                    type="text"
                    style={{ width: '100%' }}
                    onClick={toggleMode}
                    disabled={disabled}
                    aria-label="Switch to manual entry"
                  >
                    Manual entry
                    <Icon.Edit3 />
                  </Button>
                ) : null}
                <Button
                  type="text"
                  style={{ width: '100%' }}
                  onMouseDown={(event: ReactMouseEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleOpenRecord}
                  disabled={disabled || !entityId || isMultiRowSelection}
                  aria-label="View Record"
                >
                  View Record
                  <Icon.MoreVertical />
                </Button>
              </div>
            </div>
          )}
          options={options}
          labelInValue={false}
          labelRender={() => (selectedDocId ? displayName : undefined)}
          optionRender={(option) => {
            if (option.value === CREATE_RECORD_OPTION_VALUE) {
              return <CreateRecordOptionLabel label={String(option.label)} />;
            }
            const probability = optionByValue.get(String(option.value))?.probability;
            if (probability === undefined) return option.label;
            return (
              <Space.Compact style={{ width: '100%', justifyContent: 'space-between' }}>
                <span>{option.label}</span>
                <Text type="secondary">{Math.round(probability * 100)}%</Text>
              </Space.Compact>
            );
          }}
        />
        <Button
          type="text"
          tabIndex={-1}
          style={{ width: 20, color: token.colorTextQuaternary }}
          onClick={handleOpenRecord}
          disabled={mode !== 'reference' || disabled || !entityId || isMultiRowSelection}
          icon={<Icon.MoreVertical />}
          aria-label="View Record"
        />
      </Space.Compact>
    ) : (
      <Space.Compact title={title} style={{ width: '100%', ...style }}>
        <Input
          id={id}
          aria-label={`Enter manual value for ${entityScope}`}
          aria-keyshortcuts={ariaKeyshortcutsFor('field.openDetail')}
          onKeyDown={manualKeyDown}
          variant="borderless"
          style={{ flex: 1, minWidth: 0 }}
          value={manualInputValue}
          placeholder="Enter value"
          onChange={(e) => handleManualChange(e.target.value)}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <Button
          type="text"
          style={{ width: 20, color: token.colorTextQuaternary }}
          onMouseDown={(event: ReactMouseEvent<HTMLElement>) => {
            event.preventDefault();
          }}
          onClick={handleOpenEntitySidebar}
          disabled={disabled || isMultiRowSelection}
          aria-label={`Edit ${entityScope} in sidebar`}
          icon={<Icon.Edit3 />}
        />
      </Space.Compact>
    )
  );
};

export default memo(ReferenceSelect);
